import React, { FC, useState } from 'react'
import {
  TagBeta,
  ButtonClean,
  ButtonPrimary,
  Modal,
  Switch,
  Text
} from 'front-end/src/components'
import { t, Trans } from '@lingui/macro'
import { fontSizes } from '../../../../components/chakraThemeExtension'
import { notification } from '../../../../components/common/notifications/Notifications'
import { useLooopTheme } from '../../../../public-api/hooks/useLooopTheme'
import { usePublishTopicMutation } from '../../../topic-detail-page/components/topbar/TopbarEdit.codegen'

import { Box, Flex, FormControl, FormLabel, Heading } from '@chakra-ui/react'
import Textarea from 'front-end/src/components/common/form/textarea/Textarea'
import { brandColors } from '../../../../../../shared/basicPalette'

import { TooltipHelpInfo } from '../../../../components/common/tooltip/TooltipHelp'
import { usePublishModalQuery } from './PublishModal.codegen'
import { useParams } from 'react-router-dom'
import { useEffectfulState } from 'front-end/src/app/hooks/useEffectfulState'

type Props = {
  resolve: () => void
  reject: () => void
}

const bumpVersion = (version: string, isMajor: boolean = false) => {
  if (!version) {
    version = '0.0'
  }
  const majorVersion = Number(version.split('.')[0])
  const minorVersion = Number(version.split('.')[1])

  return `${isMajor ? majorVersion + 1 : majorVersion}.${
    isMajor ? 0 : minorVersion + 1
  }`
}

export const PublishModal: FC<Props> = ({ resolve, reject }) => {
  const { theme } = useLooopTheme()
  const { topicId } = useParams<{ topicId: string }>()

  const { data } = usePublishModalQuery({
    variables: {
      topicId: +topicId
    },
    fetchPolicy: 'cache-first'
  })

  const [publishFn] = usePublishTopicMutation()
  const [description, setDescription] = useState('')
  const [isMajorUpdate, setIsMajorUpdate] = useEffectfulState(
    // if we published topic before default option should be "minor" publishing
    data?.topic?.lastPublishedDate ? false : true
  )
  const [resetEnrolments, setResetEnrolments] = useState(false)

  const topic = data?.topic
  if (!topic) {
    return null
  }

  const publish = async () => {
    try {
      await publishFn({
        variables: {
          id: data?.topic?.id!,
          major: isMajorUpdate,
          resetEnrolments,
          message: description
        }
      })
      notification.info(t`Topic has been published`)
      resolve()
    } catch (e) {
      console.error(e)
      if (e.response?.data?.message) {
        notification.error(e.response.data.message)
      }
    } finally {
      reject()
    }
  }

  const getMajorVersionColor = () => {
    if (resetEnrolments) {
      return brandColors.orange.mid
    }
    if (isMajorUpdate) {
      return theme.colors.buttonPrimary
    }
  }

  return (
    <Modal
      size="WIDE"
      controls={
        <>
          <ButtonClean onClick={() => reject()}>
            <Trans>Cancel</Trans>
          </ButtonClean>
          <ButtonPrimary onClick={() => publish()}>
            <Trans>Publish</Trans>
          </ButtonPrimary>
        </>
      }
    >
      <Flex pt="32px" textAlign="left">
        <Heading size="md" mr="12px">
          <Trans>Publish changes</Trans>
        </Heading>
        <TagBeta />
      </Flex>

      <FormControl pt="4px">
        <FormLabel fontSize={fontSizes.md}>
          <Trans>Describe the changes you made (optional)</Trans>
        </FormLabel>
        <Textarea
          rows={2}
          fullWidth
          onChange={(value) => {
            setDescription(value)
          }}
          value={description}
        />
      </FormControl>

      <>
        <FormControl
          display="flex"
          alignItems="center"
          mt="16px"
          pb="8px"
          justifyContent="space-between"
        >
          {!!topic.lastPublishedDate && (
            <Flex align="center">
              <FormLabel
                w="180px"
                fontSize={fontSizes.md}
                color={brandColors.grey['900']}
                m={0}
                alignItems="center"
              >
                <Trans>Major update</Trans>
                <TooltipHelpInfo>
                  <Text align="left">
                    <Trans>
                      Default will publish a minor version for smaller changes
                      (1.1). Major should be used for more impactful changes
                      (1.0).
                    </Trans>
                  </Text>
                </TooltipHelpInfo>
              </FormLabel>
              <Switch
                onChange={() => setIsMajorUpdate(!isMajorUpdate)}
                isChecked={isMajorUpdate}
              />
            </Flex>
          )}

          <Flex color={brandColors.grey['500']} alignItems="center">
            <Trans>Version</Trans> {topic.latestPublishedTopicVersion} »{' '}
            <Box
              ml="4px"
              // ugly, but this font weight has weird offset and it looks weird
              mt="-1px"
              fontSize="18px"
              fontWeight={500}
              color={getMajorVersionColor()}
            >
              {bumpVersion(
                topic.latestPublishedTopicVersion as string,
                isMajorUpdate
              )}
            </Box>
          </Flex>
        </FormControl>

        {topic.lastPublishedDate && isMajorUpdate && (
          <FormControl display="flex" alignItems="center" py="8px">
            <FormLabel
              color={brandColors.grey['900']}
              w="180px"
              fontSize={fontSizes.md}
              display="flex"
              alignItems="center"
              transition="0"
              m={0}
            >
              <Trans>Force restart</Trans>
              <TooltipHelpInfo>
                <Text align="left">
                  <Trans>
                    When enabled, new clear enrolments will be created for users
                    that are in-progress, and their current enrolment will be
                    archived. Users with completed enrolments will not be
                    impacted.
                  </Trans>
                </Text>
              </TooltipHelpInfo>
            </FormLabel>
            <Switch
              colorScheme="warning"
              onChange={() => setResetEnrolments(!resetEnrolments)}
              isChecked={resetEnrolments}
            />
          </FormControl>
        )}
      </>
    </Modal>
  )
}
